/**
 * Разделы настроек S-02 … S-25.
 *
 * Собраны в один маршрут `/settings/$section`: у каждого раздела свой ID
 * в каталоге, но общий каркас — шапка, карточки, единые правила ошибок.
 */
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { useBlockedUserIds } from '@entities/user/model'
import { api, idempotency, ifMatch, unwrap, uuid } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import { uiStateRepo, UiKey } from '@shared/db'
import type { Settings } from '@shared/api/types'
import {
  Avatar,
  Banner,
  Button,
  Dialog,
  EmptyState,
  Field,
  Icon,
  RadioGroup,
  Spinner,
  Switch,
  Textarea,
  cx,
  useToast,
} from '@shared/ui'

import { Screen } from '../../layouts/Screen'
import { refreshMe } from '../../boot/bootstrap'
import { formatDate, formatDateTime } from '../../lib/format'
import { useAppStore } from '../../model/app-store'
import { AVAILABLE_LOCALES, resolveLocale } from '../../model/locale'
import { useThemeStore, type ThemePreference } from '../../model/theme'
import { PushStatusBanner } from '../notifications/PushPermission'
import { IdentitiesSection } from './IdentitiesSection'
import { SessionsSection } from './SessionsSection'
import styles from './settings.module.css'

const TITLES: Record<string, string> = {
  account: 'Аккаунт',
  identities: 'Способы входа',
  sessions: 'Активные входы',
  restrictions: 'Ограничения аккаунта',
  privacy: 'Приватность',
  notifications: 'Уведомления',
  blocked: 'Заблокированные',
  appearance: 'Внешний вид и язык',
  support: 'Помощь и поддержка',
  legal: 'Документы',
  export: 'Мои данные',
  delete: 'Удаление аккаунта',
}

export function SettingsSectionScreen(): React.ReactElement {
  const { section } = useParams({ strict: false }) as { section: string }

  return (
    <Screen title={TITLES[section] ?? 'Настройки'} back narrow>
      <SectionBody section={section} />
    </Screen>
  )
}

function SectionBody({ section }: { section: string }): React.ReactElement {
  switch (section) {
    case 'privacy':
      return <PrivacySection />
    case 'notifications':
      return <NotificationsSection />
    case 'appearance':
      return <AppearanceSection />
    case 'sessions':
      return <SessionsSection />
    case 'identities':
      return <IdentitiesSection />
    case 'blocked':
      return <BlockedSection />
    case 'restrictions':
      return <RestrictionsSection />
    case 'export':
      return <ExportSection />
    case 'delete':
      return <DeleteSection />
    case 'account':
      return <AccountSection />
    case 'legal':
      return <LegalSection />
    case 'support':
      return <SupportSection />
    default:
      return <EmptyState icon="settings" title="Раздел не найден" />
  }
}

/* ===================================================================== */
/* S-03 «Приватность» (ПС §13, BS §15.3)                                  */
/* ===================================================================== */

function useSettingsPatch() {
  const me = useAppStore((s) => s.me)
  const toast = useToast()
  const [saving, setSaving] = useState(false)

  const patch = async (changes: Partial<Settings>): Promise<void> => {
    if (!me) return
    setSaving(true)

    try {
      unwrap(
        await api.PATCH('/me/settings', {
          body: changes as never,
          ...ifMatch(me.settings.version),
        }),
      )
      await refreshMe()
    } catch {
      toast.show('Не удалось сохранить настройку', { tone: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  return { settings: me?.settings, patch, saving }
}

function PrivacySection(): React.ReactElement {
  const { settings, patch } = useSettingsPatch()
  if (!settings) return <Spinner size={24} />

  return (
    <>
      <div className={cx(styles['card'], styles['switchCard'])}>
        <Switch
          checked={settings.show_online}
          onCheckedChange={(value) => void patch({ show_online: value })}
          title="Показывать, что я в сети"
          hint="Видят только пользователи с активным совпадением"
        />
        <Switch
          checked={settings.show_last_seen}
          onCheckedChange={(value) => void patch({ show_last_seen: value })}
          title="Показывать время последней активности"
          hint="Показывается приблизительно, без точного времени"
        />
        <Switch
          checked={settings.read_receipts}
          onCheckedChange={(value) => void patch({ read_receipts: value })}
          title="Подтверждения прочтения"
          hint="Если выключить, вы не увидите статус «прочитано» у своих сообщений"
        />
      </div>

      {/* Изменение v1.6: телефон — как в Telegram, по выбору владельца */}
      <div className={styles['choiceCard']}>
        <Field label="Кто видит мой номер телефона">
          {() => (
            <RadioGroup
              name="phone-visibility"
              value={settings.phone_visibility ?? 'contacts'}
              onValueChange={(value) => void patch({ phone_visibility: value })}
              options={[
                { value: 'everyone', label: 'Все' },
                { value: 'contacts', label: 'Мои контакты', hint: 'Те, кого вы добавили сами' },
                { value: 'nobody', label: 'Никто' },
              ]}
            />
          )}
        </Field>
      </div>

      <div className={styles['choiceCard']}>
        <Field label="Кто может найти меня по номеру">
          {() => (
            <RadioGroup
              name="findable-by-phone"
              value={settings.findable_by_phone ?? 'everyone'}
              onValueChange={(value) => void patch({ findable_by_phone: value })}
              options={[
                { value: 'everyone', label: 'Все, кто знает номер' },
                { value: 'contacts', label: 'Только мои контакты' },
              ]}
            />
          )}
        </Field>
      </div>

      <p className={styles['footnote']}>
        Настройки взаимные: скрыли своё — не видите и чужое. Номер телефона виден только по правилу выше; в
        подборке «Движа» его нет никогда.
      </p>
    </>
  )
}

/* ===================================================================== */
/* S-04 «Уведомления» (ПС §12)                                            */
/* ===================================================================== */

function NotificationsSection(): React.ReactElement {
  const { settings, patch } = useSettingsPatch()

  if (!settings) return <Spinner size={24} />

  return (
    <>
      {/* S-04: статус разрешения устройства — N-03 при `prompt`, N-04 при `denied` */}
      <PushStatusBanner />

      <div className={cx(styles['card'], styles['switchCard'])} style={{ marginTop: 'var(--space-4)' }}>
        <Switch
          checked={settings.notif_messages}
          onCheckedChange={(value) => void patch({ notif_messages: value })}
          title="Сообщения"
        />
        <Switch
          checked={settings.notif_matches}
          onCheckedChange={(value) => void patch({ notif_matches: value })}
          title="Совпадения"
        />
        <Switch
          checked={settings.notif_reactions}
          onCheckedChange={(value) => void patch({ notif_reactions: value })}
          title="Реакции"
          hint="Низкоприоритетные уведомления"
        />
        <Switch
          checked={settings.notif_preview}
          onCheckedChange={(value) => void patch({ notif_preview: value })}
          title="Показывать текст в уведомлении"
          hint="Пока выключено, на заблокированном экране виден только факт сообщения"
        />
      </div>

      <p className={styles['footnote']}>Уведомления о безопасности аккаунта отключить нельзя.</p>
    </>
  )
}

/* ===================================================================== */
/* S-05 «Внешний вид» (CLIENT_WEB.md §3.7)                                */
/* ===================================================================== */

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: 'sun' | 'moon' | 'monitor' }[] = [
  { value: 'light', label: 'Светлая', icon: 'sun' },
  { value: 'dark', label: 'Тёмная', icon: 'moon' },
  { value: 'system', label: 'Системная', icon: 'monitor' },
]

function AppearanceSection(): React.ReactElement {
  const preference = useThemeStore((s) => s.preference)
  const setPreference = useThemeStore((s) => s.setPreference)

  return (
    <>
      <h2 className={styles['groupTitle']}>Тема оформления</h2>

      <div className={styles['card']}>
        <div className={styles['themeRow']}>
          {THEME_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cx(
                styles['themeOption'],
                preference === option.value && styles['themeOptionActive'],
              )}
              onClick={() => setPreference(option.value)}
              aria-pressed={preference === option.value}
            >
              <Icon name={option.icon} size={22} />
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <p className={styles['footnote']}>Тема хранится на этом устройстве.</p>

      <LanguageBlock />
    </>
  )
}

/**
 * S-05, часть «язык»: значение живёт в `Settings.locale` и синхронизируется
 * между устройствами (CC §13.1). В V1 сборка содержит только русский —
 * экран говорит об этом прямо, а не прячет настройку.
 */
function LanguageBlock(): React.ReactElement {
  const { settings, patch, saving } = useSettingsPatch()
  const current = resolveLocale(settings?.locale)

  return (
    <>
      <h2 className={styles['groupTitle']}>Язык интерфейса</h2>

      <div className={styles['card']}>
        {AVAILABLE_LOCALES.map((option) => (
          <button
            key={option.value}
            type="button"
            className={styles['row']}
            // Список языков доступен сразу, переключатель — только с Settings.
            disabled={!settings || saving || option.value === current}
            onClick={() => void patch({ locale: option.value })}
          >
            <span className={styles['rowIcon']}>
              <Icon name="globe" size={20} />
            </span>
            <span className={styles['rowBody']}>
              <span className={styles['rowLabel']}>{option.label}</span>
            </span>
            {option.value === current ? <Icon name="check" size={18} /> : null}
          </button>
        ))}
      </div>

      {AVAILABLE_LOCALES.length === 1 ? (
        <p className={styles['footnote']}>
          Пока доступен только русский. Язык хранится в аккаунте, а не на устройстве.
        </p>
      ) : null}
    </>
  )
}

/* ===================================================================== */
/* S-12 «Заблокированные» (ПС §14)                                        */
/* ===================================================================== */

function BlockedSection(): React.ReactElement {
  const toast = useToast()
  const localIds = useBlockedUserIds()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['blocks'],
    queryFn: async () => unwrap(await api.GET('/me/blocks')).items,
  })

  if (isLoading) return <Spinner size={24} />

  const items = data ?? []

  if (!items.length) {
    return (
      <EmptyState
        icon="block"
        title="Список пуст"
        text={
          localIds.length
            ? 'Блокировки синхронизируются, подождите секунду.'
            : 'Вы никого не блокировали. Список виден только вам.'
        }
      />
    )
  }

  return (
    <div className={styles['card']}>
      {items.map((item) => (
        <div key={item.user_id} className={styles['sessionRow']}>
          <Avatar name={item.display_name} size="md" />

          <div className={styles['sessionBody']}>
            <div className={styles['sessionName']}>{item.display_name ?? 'Профиль недоступен'}</div>
            <div className={styles['sessionMeta']}>Заблокирован {formatDateTime(item.blocked_at)}</div>
          </div>

          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              void api
                .DELETE('/users/{userId}/block', { params: { path: { userId: item.user_id } } })
                .then(() => {
                  toast.show('Блокировка снята. Совпадение не восстанавливается.')
                  void refetch()
                })
                .catch(() => toast.show('Не удалось снять блокировку', { tone: 'danger' }))
            }}
          >
            Разблокировать
          </Button>
        </div>
      ))}
    </div>
  )
}

/* ===================================================================== */
/* S-25 «Ограничения аккаунта» и SF-07 «Апелляция»                        */
/* ===================================================================== */

const SANCTION_TEXT: Record<string, string> = {
  warning: 'Предупреждение',
  read_only: 'Ограничена отправка сообщений',
  hidden: 'Профиль скрыт из рекомендаций',
  banned: 'Доступ к аккаунту заблокирован',
}

/** SF-07 «Апелляция»: текст пишет пользователь, а не код. */
const APPEAL_MAX = 2000

function RestrictionsSection(): React.ReactElement {
  const navigate = useNavigate()
  const me = useAppStore((s) => s.me)
  const toast = useToast()
  const sanctions = me?.sanctions ?? []

  const [appealFor, setAppealFor] = useState<string | null>(null)
  const [appealText, setAppealText] = useState('')
  const [sending, setSending] = useState(false)
  const [lastAppealId, setLastAppealId] = useState<string | null>(null)

  // GAP-ALL-05: списка апелляций в контракте нет — ведём последнюю сами.
  useEffect(() => {
    void uiStateRepo
      .get<string>(UiKey.lastAppealId)
      .then((value) => setLastAppealId(value))
      .catch(() => undefined)
  }, [])

  const submitAppeal = async (): Promise<void> => {
    if (!appealFor || appealText.trim().length < 10) return
    setSending(true)

    try {
      const created = unwrap(
        await api.POST('/appeals', {
          body: { sanction_id: appealFor, text: appealText.trim().slice(0, APPEAL_MAX) } as never,
          ...idempotency(uuid()),
        }),
      )

      await uiStateRepo.set(UiKey.lastAppealId, created.id)
      setLastAppealId(created.id)
      setAppealFor(null)
      setAppealText('')
      toast.show('Апелляция отправлена')
    } catch (error) {
      // 409 `appeal_exists`: апелляция по этой санкции уже есть.
      if (error instanceof ApiError && error.code === 'appeal_exists') {
        setAppealFor(null)
        toast.show('Апелляция по этому ограничению уже рассматривается')
        return
      }
      toast.show('Не удалось отправить апелляцию', { tone: 'danger' })
    } finally {
      setSending(false)
    }
  }

  if (!sanctions.length) {
    return <EmptyState icon="check" title="Ограничений нет" text="Аккаунт работает в обычном режиме." />
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {lastAppealId ? (
        <Banner
          tone="neutral"
          icon="info"
          actions={
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void navigate({ to: '/appeals/$appealId', params: { appealId: lastAppealId } })}
            >
              Статус апелляции
            </Button>
          }
        >
          Вы уже отправляли апелляцию.
        </Banner>
      ) : null}

      {sanctions.map((sanction) => (
        <Banner
          key={sanction.id}
          tone="warning"
          title={SANCTION_TEXT[sanction.type] ?? 'Ограничение аккаунта'}
        >
          {sanction.ends_at ? `Действует до ${formatDateTime(sanction.ends_at)}.` : 'Действует бессрочно.'}

          {sanction.appealable ? (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Button size="sm" variant="secondary" onClick={() => setAppealFor(sanction.id)}>
                Обжаловать
              </Button>
            </div>
          ) : null}
        </Banner>
      ))}

      {/* --- SF-07 «Апелляция» --- */}
      <Dialog
        open={appealFor !== null}
        onClose={() => setAppealFor(null)}
        title="Обжаловать ограничение"
        description="Опишите, почему считаете решение ошибочным. Это читает человек."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAppealFor(null)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={sending}
              disabled={appealText.trim().length < 10}
              onClick={() => void submitAppeal()}
            >
              Отправить
            </Button>
          </>
        }
      >
        <Textarea
          value={appealText}
          onChange={(event) => setAppealText(event.target.value.slice(0, APPEAL_MAX))}
          placeholder="Что произошло и почему ограничение кажется ошибкой"
          rows={5}
          maxLength={APPEAL_MAX}
        />
      </Dialog>
    </div>
  )
}

/* ===================================================================== */
/* S-23 «Экспорт данных» (ПС §13)                                         */
/* ===================================================================== */

function ExportSection(): React.ReactElement {
  const toast = useToast()
  const [requesting, setRequesting] = useState(false)

  return (
    <>
      <p className={styles['footnote']} style={{ marginTop: 0 }}>
        Архив придёт уведомлением. Ссылка живёт 24 часа.
      </p>

      <div style={{ marginTop: 'var(--space-4)' }}>
        <Button
          variant="primary"
          fullWidth
          loading={requesting}
          onClick={() => {
            setRequesting(true)
            void api
              .POST('/me/exports', { ...idempotency(uuid()) })
              .then(() => toast.show('Запрос принят. Пришлём уведомление, когда архив будет готов.'))
              .catch(() => toast.show('Запросить экспорт можно не чаще раза в неделю', { tone: 'danger' }))
              .finally(() => setRequesting(false))
          }}
        >
          Запросить архив
        </Button>
      </div>
    </>
  )
}

/* ===================================================================== */
/* S-20 «Удаление аккаунта» (ПС §15)                                      */
/* ===================================================================== */

function DeleteSection(): React.ReactElement {
  const navigate = useNavigate()
  const toast = useToast()
  const [confirmOpen, setConfirmOpen] = useState(false)

  return (
    <>
      <Banner tone="danger" title="Что произойдёт при удалении">
        Профиль исчезнет из рекомендаций, совпадения закроются, новые сообщения будут невозможны. В старых
        диалогах собеседники увидят «Удалённый аккаунт». Через 14 дней ваши каналы и группы будут удалены, а
        никнейм освободится. До этого срока восстановить аккаунт можно через поддержку.
      </Banner>

      {/* ПС §15: предлагаем паузу как альтернативу, но без давления */}
      <div className={styles['card']} style={{ marginTop: 'var(--space-5)' }}>
        <div style={{ padding: 'var(--space-4)' }}>
          <div className={styles['rowLabel']}>Может быть, достаточно паузы?</div>
          <p className={styles['cardText']}>
            Анкета скроется из подборки, а чаты, контакты и настройки останутся. Вернуться — одной кнопкой во
            вкладке «Движ».
          </p>

          <div style={{ marginTop: 'var(--space-3)' }}>
            <Button
              variant="secondary"
              onClick={() => {
                void api
                  .POST('/me/pause')
                  .then(async () => {
                    await refreshMe()
                    toast.show('Профиль поставлен на паузу')
                    void navigate({ to: '/me' })
                  })
                  .catch(() => toast.show('Не удалось поставить на паузу', { tone: 'danger' }))
              }}
            >
              Поставить на паузу
            </Button>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 'var(--space-6)' }}>
        <Button variant="danger" fullWidth onClick={() => setConfirmOpen(true)}>
          Удалить аккаунт
        </Button>
      </div>

      {/* --- S-21 «Подтверждение удаления»: повторное явное действие --- */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        dismissOnScrim={false}
        title="Удалить аккаунт?"
        description="Через 14 дней аккаунт и принадлежащие вам каналы и группы будут удалены окончательно. История сообщений у собеседников сохранится."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setConfirmOpen(false)
                void api
                  .POST('/me/delete', { body: { confirm: true } as never, ...idempotency(uuid()) })
                  .then(() => toast.show('Запрос на удаление принят'))
                  .catch(() => toast.show('Не удалось удалить аккаунт', { tone: 'danger' }))
              }}
            >
              Удалить
            </Button>
          </>
        }
      />
    </>
  )
}

/* ===================================================================== */
/* S-02 «Аккаунт» и S-14 «Документы»                                      */
/* ===================================================================== */

function AccountSection(): React.ReactElement {
  const me = useAppStore((s) => s.me)
  const navigate = useNavigate()

  return (
    <>
      <div className={styles['card']}>
        <div className={styles['sessionRow']}>
          <Icon name="user" size={22} />
          <div className={styles['sessionBody']}>
            <div className={styles['sessionName']}>{me?.profile.display_name ?? 'Без имени'}</div>
            <div className={styles['sessionMeta']}>
              Аккаунт создан {me ? formatDateTime(me.user.created_at) : ''}
            </div>
          </div>
        </div>
      </div>

      <div className={styles['card']} style={{ marginTop: 'var(--space-4)' }}>
        <button type="button" className={styles['row']} onClick={() => void navigate({ to: '/me/edit' })}>
          <span className={styles['rowIcon']}>
            <Icon name="edit" size={20} />
          </span>
          <span className={styles['rowBody']}>
            <span className={styles['rowLabel']}>Редактировать профиль</span>
          </span>
          <span className={styles['rowChevron']}>
            <Icon name="chevron-right" size={18} />
          </span>
        </button>
      </div>

      {/*
        Изменение v1.12: паузы профиля здесь больше нет. Она делала ровно то
        же, что выключенный подбор — не показывала анкету и не давала колоду, —
        и была вторым переключателем для одного состояния. Участие в подборе
        включается там, где им пользуются: во вкладке «Движ».
      */}
      <p className={styles['footnote']}>
        Показывать анкету в «Движе» или нет — переключатель во вкладке «Движ», в параметрах подбора.
      </p>
    </>
  )
}

/** S-16 «Помощь и поддержка»: обращение в поддержку и документы. */
function SupportSection(): React.ReactElement {
  const navigate = useNavigate()

  const rows: { label: string; hint: string; icon: 'chats' | 'shield'; to: () => void }[] = [
    {
      label: 'Написать в поддержку',
      hint: 'Ответим на почту или в уведомлениях',
      icon: 'chats',
      to: () => void navigate({ to: '/settings/support/new' }),
    },
    {
      label: 'Правила сообщества',
      hint: 'Что можно и что нельзя',
      icon: 'shield',
      to: () => void navigate({ to: '/legal/$document', params: { document: 'community_rules' } }),
    },
  ]

  return (
    <>
      <div className={styles['card']}>
        {rows.map((row) => (
          <button key={row.label} type="button" className={styles['row']} onClick={row.to}>
            <span className={styles['rowIcon']}>
              <Icon name={row.icon} size={20} />
            </span>
            <span className={styles['rowText']}>
              <span className={styles['rowLabel']}>{row.label}</span>
              <span className={styles['rowHint']}>{row.hint}</span>
            </span>
            <span className={styles['rowChevron']}>
              <Icon name="chevron-right" size={18} />
            </span>
          </button>
        ))}
      </div>
    </>
  )
}

function LegalSection(): React.ReactElement {
  const navigate = useNavigate()
  const config = useAppStore((s) => s.config)

  const documents: { id: 'terms' | 'privacy' | 'community_rules'; label: string }[] = [
    { id: 'terms', label: 'Условия использования' },
    { id: 'privacy', label: 'Политика приватности' },
    { id: 'community_rules', label: 'Правила сообщества' },
  ]

  return (
    <div className={styles['card']}>
      {documents.map((document) => (
        <button
          key={document.id}
          type="button"
          className={styles['row']}
          onClick={() => void navigate({ to: '/legal/$document', params: { document: document.id } })}
        >
          <span className={styles['rowIcon']}>
            <Icon name="shield" size={20} />
          </span>
          <span className={styles['rowText']}>
            <span className={styles['rowLabel']}>{document.label}</span>
            {config?.legal?.[document.id] ? (
              <span className={styles['rowHint']}>
                Редакция от {formatDate(config.legal[document.id].version)}
              </span>
            ) : null}
          </span>
          <span className={styles['rowChevron']}>
            <Icon name="chevron-right" size={18} />
          </span>
        </button>
      ))}
    </div>
  )
}
